import { BroadcastChannel } from 'broadcast-channel';
import { isClientGeneratedSmid } from './hpStepController';

/**
 * Registry of clinical step creates that have been written optimistically but not yet reconciled
 * with the backend.
 *
 * Exists so `useHangingProtocol` can tell "the user just added a step and we are waiting on the
 * server" apart from "these steps happen to carry client-generated smids". Inferring it from the
 * smid shape alone is not good enough: a freshly created protocol also holds `step-{uuid}` smids
 * before its first reconcile, and treating that as pending suppressed the re-initialization that
 * resolves viewports, leaving them with nothing hung (caught by the HP classification-criteria live
 * E2E while fixing VX-4811).
 *
 * Module-level rather than Recoil or a ref: several `useHangingProtocol` instances are mounted at
 * once (widget, layouts, viewport overlays), the create is registered by a different hook
 * (`useClinicalStepOperations`), and the flag has to survive a StepBar remount mid-create.
 *
 * ## Why this is broadcast across windows
 *
 * A create is optimistic and its mutation is deferred, so for a second or two the step exists in
 * the window that added it and nowhere else. The *other* window is still free to run
 * `applyFetchedSteps` with the server's pre-add list, and when it does, both windows write
 * `steppedHPConfigurations` from the same base version and mint the same version number.
 * `steppedHPMergeFn` only accepts a strictly higher version, so each rejects the other and the
 * windows silently diverge -- one holding two steps, the other one.
 *
 * The damage shows up somewhere else entirely. `hangingProtocolStepIndex` is plain last-write-wins
 * across windows, so the window still holding one step drags the active index back to 0, and the
 * next layout edit lands on the wrong step. Measured directly: viewer/0 `[W] v8->9 n=2`, viewer/1
 * `[W] v8->9 n=1` 95ms later, viewer/0 `[IDX] 1->0` 150ms after that, and the following widening
 * written into step 0 while the radiologist was looking at step 1.
 *
 * Keeping the registry window-local is what allowed that: viewer/0 knew a create was outstanding
 * and viewer/1 did not. Broadcasting it means both windows hold off re-initializing from a server
 * response that cannot describe the step yet.
 */

type PendingCreateMessage =
  | { type: 'begin'; smid: string }
  | { type: 'end'; smid: string }
  | { type: 'clear' }
  | { type: 'delete-begin'; smid: string }
  | { type: 'delete-end'; smid: string };

/** Creates started in this window. */
const localPendingSmids = new Set<string>();

/**
 * Creates started in another window, mapped to the point at which we stop believing in them.
 *
 * Expiry is not optional. A remote entry is only ever retired by an `end` message, and a window
 * that is closed, reloaded, or crashes mid-create never sends one. Without a deadline that entry
 * would suppress `applyFetchedSteps` in every surviving window for the rest of the session, which
 * breaks HP initialization outright -- a far worse failure than the race being fixed. The window is
 * generous relative to a create round-trip and short relative to a reading session.
 */
const remotePendingExpiries = new Map<string, number>();

const REMOTE_PENDING_TTL_MS = 30_000;

/**
 * Steps whose deletion has been requested but not yet committed, mapped the same way as
 * `remotePendingExpiries`. Both windows need this: the deleting window defers the mutation behind
 * the undo window, so for a couple of seconds the step is gone locally and still present on the
 * server, and a peer that refetches in that gap sees a list that legitimately still contains it.
 */
const localPendingDeletes = new Set<string>();
const remotePendingDeleteExpiries = new Map<string, number>();

/**
 * Bumped whenever the pending-delete set changes, local or remote, so a consumer can re-run work that
 * depends on it.
 *
 * Bucket pruning needs this. It spares a step whose deletion has not committed, and the invariant is
 * that the step must be pruned once the delete is no longer restorable -- but nothing the prune effect
 * otherwise depends on changes when a deferred mutation resolves, so without a signal the spared
 * bucket outlived the deletion in every window that was not the one clearing it. Mirrors
 * `subscribeToInvalidationRefetches` in `hpInitMachine.ts`, which exists for the same reason.
 */
let pendingStepDeleteVersion = 0;
const pendingStepDeleteSubscribers = new Set<() => void>();

function notePendingStepDeletesChanged(): void {
  pendingStepDeleteVersion += 1;
  for (const notify of pendingStepDeleteSubscribers) notify();
}

export const getPendingStepDeleteVersion = (): number => pendingStepDeleteVersion;

export const subscribeToPendingStepDeletes = (onChange: () => void): (() => void) => {
  pendingStepDeleteSubscribers.add(onChange);
  return () => {
    pendingStepDeleteSubscribers.delete(onChange);
  };
};

/**
 * Notifies only when the pending-delete set actually changed.
 *
 * Every notify schedules a React render in each mounted `useHangingProtocol`, so a call that clears
 * nothing must stay silent -- an HP switch with no deletion outstanding is the common case, and
 * waking subscribers there is both pointless and, from a call stack that is not inside `act`, enough
 * to fail a test on React's act warning.
 */
function notePendingStepDeletesChangedIfAny(hadEntries: boolean): void {
  if (hadEntries) notePendingStepDeletesChanged();
}

/** True while either delete registry holds anything, for the guard above. */
const hasAnyPendingDelete = (): boolean =>
  localPendingDeletes.size > 0 || remotePendingDeleteExpiries.size > 0;

/**
 * TTL wake-ups for remote pending deletes, keyed by smid.
 *
 * Expiry is otherwise lazy -- it happens when a reader next calls through `pruneExpired` -- and that
 * is not enough on its own. If the window that began the deletion crashes or is closed, no
 * `delete-end` ever arrives, and nothing else changes the prune effect's inputs, so this window keeps
 * sparing that step's bucket well past the TTL. Waking subscribers *from* the lazy expiry is not an
 * option: it runs inside `isPendingStepDelete` and `pendingStepDeleteSmids`, both of which are called
 * from the prune effect itself, and notifying mid-render is how you get "cannot update a component
 * while rendering a different component". A timer fires outside any render, so it can.
 */
const remoteDeleteExpiryTimers = new Map<string, ReturnType<typeof setTimeout>>();

function cancelRemoteDeleteExpiryWake(smid: string): void {
  const timer = remoteDeleteExpiryTimers.get(smid);
  if (timer == null) return;
  clearTimeout(timer);
  remoteDeleteExpiryTimers.delete(smid);
}

function scheduleRemoteDeleteExpiryWake(smid: string): void {
  cancelRemoteDeleteExpiryWake(smid);
  remoteDeleteExpiryTimers.set(
    smid,
    // Slightly past the deadline so the entry reads as expired rather than exactly at it.
    setTimeout(() => {
      remoteDeleteExpiryTimers.delete(smid);
      const had = hasAnyPendingDelete();
      pruneExpired(remotePendingDeleteExpiries);
      notePendingStepDeletesChangedIfAny(had && !hasAnyPendingDelete());
    }, REMOTE_PENDING_TTL_MS + 100)
  );
}

function cancelAllRemoteDeleteExpiryWakes(): void {
  for (const timer of remoteDeleteExpiryTimers.values()) clearTimeout(timer);
  remoteDeleteExpiryTimers.clear();
}

const pendingCreateChannel = new BroadcastChannel<PendingCreateMessage>('hp-pending-step-creates');

/**
 * Applies a message from another window. Exported so tests can exercise the real bookkeeping
 * without depending on channel delivery, which the test environment's BroadcastChannel stub does
 * not perform.
 */
export function applyRemotePendingCreateMessage(message?: PendingCreateMessage): void {
  if (message == null) return;
  if (message.type === 'begin') {
    remotePendingExpiries.set(message.smid, Date.now() + REMOTE_PENDING_TTL_MS);
    return;
  }
  if (message.type === 'end') {
    remotePendingExpiries.delete(message.smid);
    return;
  }
  if (message.type === 'delete-begin') {
    remotePendingDeleteExpiries.set(message.smid, Date.now() + REMOTE_PENDING_TTL_MS);
    scheduleRemoteDeleteExpiryWake(message.smid);
    notePendingStepDeletesChanged();
    return;
  }
  if (message.type === 'delete-end') {
    remotePendingDeleteExpiries.delete(message.smid);
    cancelRemoteDeleteExpiryWake(message.smid);
    notePendingStepDeletesChanged();
    return;
  }
  // A `clear` from the window that switched HP. Unlike that window, *this* one's `hpKey` has not
  // changed, so nothing else will re-run its prune -- if this drops a pending delete, the bucket that
  // was being spared for it has to be reconsidered now or it is stranded until the case is reopened.
  const hadPendingDeletes = hasAnyPendingDelete();
  remotePendingExpiries.clear();
  remotePendingDeleteExpiries.clear();
  cancelAllRemoteDeleteExpiryWakes();
  notePendingStepDeletesChangedIfAny(hadPendingDeletes);
}

// addEventListener rather than the single onmessage slot: this module is imported by several
// hooks, and assigning onmessage from more than one place clobbers the previous handler -- the
// failure mode behind VX-4430 on the hp-invalidation channel.
pendingCreateChannel.addEventListener('message', applyRemotePendingCreateMessage);

/** Drops remote entries whose originating window never reported back. */
function pruneExpired(entries: Map<string, number>): void {
  if (entries.size === 0) return;
  const now = Date.now();
  for (const [smid, expiresAt] of entries) {
    if (expiresAt <= now) entries.delete(smid);
  }
}

/**
 * Records an optimistic step whose create has not yet resolved.
 *
 * `Nullable` because `StepConfiguration.smid` is; a step with no smid cannot be tracked and cannot
 * be reconciled either, so it is simply ignored.
 */
export function beginPendingStepCreate(smid: Nullable<string>): void {
  if (smid == null) return;
  localPendingSmids.add(smid);
  pendingCreateChannel.postMessage({ type: 'begin', smid });
}

/** Clears an optimistic step once its create has resolved, been rolled back, or been discarded. */
export function endPendingStepCreate(smid: Nullable<string>): void {
  if (smid == null) return;
  localPendingSmids.delete(smid);
  pendingCreateChannel.postMessage({ type: 'end', smid });
}

/**
 * True while any optimistic step write -- a create or a save -- is outstanding in any window,
 * meaning a protocol fetched from the server cannot yet describe the steps that exist, or describes
 * them at a version the user has already moved past.
 */
export function hasPendingStepCreates(): boolean {
  if (localPendingSmids.size > 0) return true;
  pruneExpired(remotePendingExpiries);
  return remotePendingExpiries.size > 0;
}

/**
 * The same question asked of one step rather than of the protocol.
 *
 * `hasPendingStepCreates` is deliberately all-or-nothing: it gates re-initializing the whole step
 * list, and a list is only trustworthy as a whole. The baseline is not like that. It is written
 * per entry, and `applyServerBaselineOnly` exists because holding *all* of it back during a create
 * costs the comprehensive live spec half its phases -- a save that flushed during someone else's
 * create needs its baseline to advance.
 *
 * So the baseline needs the per-step form: advance every entry the server can speak for, and hold
 * only the ones whose own write is still outstanding. Reads the same registry, including the
 * broadcast remote entries, so a save deferred in the *other* window counts here too -- which is
 * the case that matters, since that is the window whose refetch returns the pre-PATCH protocol.
 */
export function isPendingStepWrite(smid: Nullable<string>): boolean {
  if (smid == null) return false;
  if (localPendingSmids.has(smid)) return true;
  pruneExpired(remotePendingExpiries);
  return remotePendingExpiries.has(smid);
}

/**
 * Records a clinical save whose PATCH has not yet landed.
 *
 * Shares the create registry rather than adding a parallel one, because the consumers ask the same
 * question of both: can a protocol just fetched from the server be trusted to describe the steps?
 * While a save is deferred behind the undo window it cannot -- the server still holds the pre-save
 * step -- and re-initializing from it reverts the edit the user just made.
 *
 * The window this closes is not the deferral alone. `executeSaveOnBackend` patches the Apollo cache
 * and drops the `GET_BASE_VIEWER_DATA` precache once the mutation resolves, so a fetch is at its
 * most likely exactly when the step is mid-update. Without this, `stepped-hp-priors-per-step` stored
 * every step's layout one widening behind: the clone read a step that a re-init had just reverted.
 */
export function beginPendingStepSave(smid: Nullable<string>): void {
  beginPendingStepCreate(smid);
}

/** Clears a save once its mutation has resolved, failed, or been cancelled by UNDO. */
export function endPendingStepSave(smid: Nullable<string>): void {
  endPendingStepCreate(smid);
}

/** Records a step deletion that has been requested but whose mutation has not yet fired. */
export function beginPendingStepDelete(smid: Nullable<string>): void {
  if (smid == null) return;
  localPendingDeletes.add(smid);
  notePendingStepDeletesChanged();
  pendingCreateChannel.postMessage({ type: 'delete-begin', smid });
}

/** Clears a pending deletion once it has committed or been undone. */
export function endPendingStepDelete(smid: Nullable<string>): void {
  if (smid == null) return;
  localPendingDeletes.delete(smid);
  notePendingStepDeletesChanged();
  pendingCreateChannel.postMessage({ type: 'delete-end', smid });
}

/**
 * True when this step is being deleted by some window.
 *
 * Used to tell a legitimately shrinking protocol from a stale read of it: a fetched step list that
 * is missing a step we hold means "our fetch is behind" unless that step is on its way out.
 */
export function isPendingStepDelete(smid: Nullable<string>): boolean {
  if (smid == null) return false;
  if (localPendingDeletes.has(smid)) return true;
  pruneExpired(remotePendingDeleteExpiries);
  return remotePendingDeleteExpiries.has(smid);
}

/**
 * Every step some window is currently deleting.
 *
 * The set form, for callers that have to treat a step as still present rather than ask about one they
 * are already holding. Bucket pruning is the case: it works from the live step list, and a step whose
 * deletion has not committed has already left that list while still being restorable by UNDO or Reset
 * All -- so the predicate above cannot help, the smid has to be added back in.
 *
 * Includes remote entries for the same reason `isPendingStepDelete` does: the deletion may be pending
 * in the other window, and this window's prune would still be the one to drop the bucket.
 */
export function pendingStepDeleteSmids(): string[] {
  pruneExpired(remotePendingDeleteExpiries);
  return [...new Set([...localPendingDeletes, ...remotePendingDeleteExpiries.keys()])];
}

/**
 * Drops every entry. Used when the active HP changes -- a create still in flight belongs to the HP
 * the user just left, and leaving it registered would cancel the new HP's initialization outright
 * (`useHangingProtocol` arms its init guard before applying, so nothing retries).
 *
 * Clears remote entries too, and tells the other windows to do the same: an HP switch invalidates
 * an outstanding create everywhere, not just in the window that noticed.
 *
 * Not for hook teardown: several `useClinicalStepOperations` instances are mounted at once, and one
 * of them unmounting mid-create must not clear the guard the others still need.
 */
export function clearPendingStepCreates(): void {
  localPendingSmids.clear();
  remotePendingExpiries.clear();
  const hadPendingDeletes = hasAnyPendingDelete();
  localPendingDeletes.clear();
  remotePendingDeleteExpiries.clear();
  cancelAllRemoteDeleteExpiryWakes();
  // Guarded rather than unconditional. `hpKey` does cover the window that switched, but this also runs
  // with deletions outstanding, and then the spared buckets need reconsidering. Silent when there was
  // nothing pending, which is the common case and the one that would otherwise schedule a render from
  // a call stack no test wraps in `act`.
  notePendingStepDeletesChangedIfAny(hadPendingDeletes);
  pendingCreateChannel.postMessage({ type: 'clear' });
}

/** Test seam: drops all state without broadcasting, so one test cannot leak into the next. */
export function resetPendingStepCreatesForTest(): void {
  localPendingSmids.clear();
  remotePendingExpiries.clear();
  localPendingDeletes.clear();
  remotePendingDeleteExpiries.clear();
  cancelAllRemoteDeleteExpiryWakes();
  // Deliberately does *not* notify subscribers, unlike every other mutation of these sets. This runs
  // from test teardown, where the component tree is usually still mounted, and waking a
  // `useSyncExternalStore` subscriber there schedules a React update outside `act()`. Doing so failed
  // all 14 tests in `useHangingProtocol.test.tsx`'s init-guard describe at once. Nothing needs the
  // notification: a test seam only has to leave the registry empty for the next test.
}

/**
 * True when a fetched step list is behind the one already held, and applying it would destroy a
 * step rather than reflect a real change.
 *
 * The version counter on `steppedHPConfigurations` orders writes but says nothing about freshness,
 * so a stale write wins simply by being later. That is how a step disappears: a window that loaded
 * the case before a step existed re-publishes its old copy, the merge accepts it because the
 * version is higher, and both windows lose the step until a refresh (VX-4770, and again through the
 * edit-form reopen path).
 *
 * A step is judged lost, rather than legitimately removed, when the live list holds it with a
 * server-issued smid and the fetch does not:
 *
 *  - a **server smid** means the backend has acknowledged it, so a response that omits it is either
 *    older than the create or describes a deletion. Client-generated smids are excluded: those are
 *    optimistic steps the server has genuinely never seen, and the pending-create guard covers them.
 *  - a **pending deletion** is the legitimate case, and the only one. Deletes are deferred behind
 *    the undo window and deliberately do not broadcast an HP invalidation, so a peer refetching in
 *    that gap sees the step still present; `isPendingStepDelete` is what distinguishes "on its way
 *    out" from "we simply have not caught up".
 */
export function fetchedStepsWouldLoseSteps({
  fetchedSteps,
  liveSteps,
}: {
  fetchedSteps: ReadonlyArray<{ smid: Nullable<string> }> | null;
  liveSteps: ReadonlyArray<{ smid: Nullable<string> }> | null;
}): boolean {
  if (fetchedSteps == null || liveSteps == null || liveSteps.length === 0) return false;

  const fetchedSmids = new Set(
    fetchedSteps.map(({ smid }) => smid).filter((smid): smid is string => smid != null)
  );

  // Only meaningful when both sides describe the same protocol. Switching to a different HP replaces
  // every step with an unrelated set, which shares no smids and is not a loss -- without this the
  // guard would refuse to load the HP the user just selected.
  const describesSameProtocol = liveSteps.some(
    ({ smid }) => smid != null && fetchedSmids.has(smid)
  );
  if (!describesSameProtocol) return false;

  return liveSteps.some(({ smid }) => {
    if (smid == null) return false;
    if (isClientGeneratedSmid(smid)) return false;
    if (fetchedSmids.has(smid)) return false;
    return !isPendingStepDelete(smid);
  });
}
